import { Observable } from 'rxjs';
import { retry } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_RETRY_COUNT, API_RETRY_DELAY, CACHE_TIMEOUT } from '@shared/constants';
import { withAPICache } from '@shared/interceptors';

import { ConfigService } from '../core/config.service';

const dataURLtoFile2 = (b64Data, contentType = '', sliceSize = 512) => {
	const byteCharacters = atob(b64Data);
	const byteArrays = [];

	for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
		const slice = byteCharacters.slice(offset, offset + sliceSize);

		const byteNumbers = new Array(slice.length);
		for (let i = 0; i < slice.length; i++) {
			byteNumbers[i] = slice.charCodeAt(i);
		}

		const byteArray = new Uint8Array(byteNumbers);
		byteArrays.push(byteArray);
	}

	const blob = new Blob(byteArrays, { type: contentType });
	return blob;
};

@Injectable({ providedIn: 'root' })
export class InsuranceService {
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - constructor
	 **-------------------------------------------------------------------------------------
	 */
	constructor(
		private readonly http: HttpClient,
		private readonly env: ConfigService
	) {}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - assignEmployeeToReservation
	 * *-------------------------------------------------------------------------------------
	 */
	public getInsuranceCodeByCategory({ category, companyId }): Observable<any> {
		const insuranceServiceUrl = this.env.get<string>('insuranceEndPoint');
		const url = `${insuranceServiceUrl}/public/codes/${category}`;
		return this.http
			.get(url, {
				context: withAPICache({
					ttl: CACHE_TIMEOUT,
				}),
			})
			.pipe(retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInsuranceQuotes
	 **-------------------------------------------------------------------------------------
	 */
	public getInsuranceQuoteConfigurations(): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/configuration`;
		return this.http.get(url).pipe(retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInsuranceProductConfigurations
	 **-------------------------------------------------------------------------------------
	 */
	public getInsuranceProductConfigurations(productType): Observable<any> {
		const quotesConfigServiceUrl = this.env.get<string>('insuranceQuotesConfigEndPoint');
		const url = `${quotesConfigServiceUrl}/${productType}/configuration`;
		return this.http.get(url).pipe(retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInsuranceProductConfigurationsForCompany
	 **-------------------------------------------------------------------------------------
	 */
	public getInsuranceProductConfigurationsForCompany(facilityGuid, productType, companyId, rules): Observable<any> {
		const quotesConfigServiceUrl = this.env.get<string>('insuranceQuotesConfigEndPoint');
		const url = `${quotesConfigServiceUrl}/${productType}/configuration/company/${companyId}`;
		return this.http.post(url, { rules }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInsuranceQuoteById
	 **-------------------------------------------------------------------------------------
	 */
	public getInsuranceQuoteById(quoteId): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/${quoteId}`;
		return this.http.get(url).pipe(retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInsuranceQuotesByReservationGuid
	 **-------------------------------------------------------------------------------------
	 */
	public getInsuranceQuotesByReservationGuid(reservationGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/reservation/${reservationGuid}`;
		return this.http.get(url).pipe(retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getSelectedInsuranceQuotesByReservationGuid
	 **-------------------------------------------------------------------------------------
	 */
	public getSelectedInsuranceQuotesByReservationGuid(reservationGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/reservation/${reservationGuid}/selected`;
		return this.http.get(url).pipe(retry({ count: API_RETRY_COUNT, delay: API_RETRY_DELAY }));
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getRegistrationInsuranceQuotes
	 ** v1 TODO: phase this out when the integration of the new endpoints is stable
	 **-------------------------------------------------------------------------------------
	 */
	public getRegistrationInsuranceQuotes(facilityGuid, reservationQuoteData): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/create`;
		return this.http.post(url, { ...reservationQuoteData }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getRegistrationProductInsuranceQuotes
	 ** v2
	 **-------------------------------------------------------------------------------------
	 */
	public getRegistrationProductInsuranceQuotes(facilityGuid, reservationQuoteData): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/create/product`;
		return this.http.post(url, { ...reservationQuoteData }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInsuranceProductQuotesDetails
	 **-------------------------------------------------------------------------------------
	 */
	public getInsuranceProductQuotesDetails(quoteType, reservationQuoteData, facilityGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/create/product/${quoteType}`;
		return this.http.post(url, { ...reservationQuoteData }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getLaunchInsuranceQuotes
	 ** v1 TODO: phase this out when the integration of the new endpoints is stable
	 **-------------------------------------------------------------------------------------
	 */
	public getLaunchInsuranceQuotes(facilityGuid, reservationQuoteData, quoteGuid, reservationGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/launch`;
		return this.http.post(url, { quoteGuid, reservationGuid, ...reservationQuoteData }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getLaunchProductInsuranceQuotes
	 ** v2
	 **-------------------------------------------------------------------------------------
	 */
	public getLaunchProductInsuranceQuotes(facilityGuid, reservationQuoteData, quoteGuid, reservationGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/launch/product`;
		return this.http.post(url, { quoteGuid, reservationGuid, ...reservationQuoteData }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveSelectedInsuranceQuote
	 **-------------------------------------------------------------------------------------
	 */
	public saveSelectedInsuranceQuote(facilityGuid, quoteGuid, reservationGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/${quoteGuid}`;
		return this.http.put(url, { selection: true, reservationGuid }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveSelectedInsuranceQuoteWithManifestData
	 **-------------------------------------------------------------------------------------
	 */
	public saveSelectedInsuranceQuoteWithManifestData(facilityGuid, quoteGuid, reservationGuid, manifestData): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/${quoteGuid}/selection`;
		return this.http.put(url, { selection: true, reservationGuid, manifestData }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveLaunchSelectedInsuranceQuote
	 ** v1 TODO: phase this out when the integration of the new endpoints is stable
	 **-------------------------------------------------------------------------------------
	 */
	public saveLaunchSelectedInsuranceQuote(facilityGuid, quoteGuid, reservationGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/${quoteGuid}/launch`;
		return this.http.put(url, { selection: true, reservationGuid }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveLaunchSelectedProductInsuranceQuote
	 ** v2
	 **-------------------------------------------------------------------------------------
	 */
	public saveLaunchSelectedProductInsuranceQuote(facilityGuid, quoteGuid, reservationGuid, rules, company): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/${quoteGuid}/launch/save`;
		return this.http.put(url, { selection: true, reservationGuid, rules, company }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveLaunchSelectedProductInsuranceQuoteWithManifesData
	 ** v3
	 **-------------------------------------------------------------------------------------
	 */
	public saveLaunchSelectedProductInsuranceQuoteWithManifesData(
		facilityGuid,
		quoteGuid,
		reservationGuid,
		rules,
		company,
		manifestData
	): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/${quoteGuid}/launch/update`;
		return this.http.put(
			url,
			{ selection: true, reservationGuid, rules, company, payload: manifestData },
			{ headers: { 'x-facility-guid': facilityGuid } }
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInsurancePolicy
	 **-------------------------------------------------------------------------------------
	 */
	public getInsurancePolicy(quoteId, facilityGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/${quoteId}/policy`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - submitInsurancePayment
	 **-------------------------------------------------------------------------------------
	 */
	public submitInsurancePayment(paymentData): Observable<any> {
		const paymentsServiceUrl = this.env.get<string>('insurancePaymentsEndPoint');
		const url = `${paymentsServiceUrl}/create`;
		return this.http.post(url, { ...paymentData }, { headers: { 'x-facility-guid': paymentData.facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getReservationInsurancePayment
	 **-------------------------------------------------------------------------------------
	 */
	public getReservationInsurancePayment(reservationGuid, facilityGuid): Observable<any> {
		const paymentsServiceUrl = this.env.get<string>('insurancePaymentsEndPoint');
		const url = `${paymentsServiceUrl}/reservation/${reservationGuid}`;
		return this.http.get(url);
	}
	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInsurancePolicyByReservationGuid
	 **-------------------------------------------------------------------------------------
	 */
	public getInsurancePolicyByReservationGuid(insuranceReservationGuid, facilityGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/reservation/${insuranceReservationGuid}/policy`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getInsurancePolicyByReservationGuidProduct
	 **-------------------------------------------------------------------------------------
	 */
	public getInsurancePolicyByReservationGuidProduct(reservationGuid, productId, facilityGuid): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/reservation/${reservationGuid}/product/${productId}/policy`;
		return this.http.get(url);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - declineInsurance
	 **-------------------------------------------------------------------------------------
	 */
	public declineInsurance(facilityGuid, declineData): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/selection/decline`;
		console.log('The url: ', url);
		return this.http.post(
			url,
			{
				...declineData,
			},
			{ headers: { 'x-facility-guid': facilityGuid } }
		);
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - uploadDeclineSignature
	 **-------------------------------------------------------------------------------------
	 */
	public uploadDeclineSignature(facilityGuid, fileData) {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/selection/decline/signature`;
		return this.http.post(url, fileData, {
			headers: {
				'x-facility-guid': facilityGuid,
				'x-env-name': this.env.get<string>('env'),
			},
			observe: 'response',
		});
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - getProductInsuranceQuotes
	 **-------------------------------------------------------------------------------------
	 */
	public getProductInsuranceQuotes(facilityGuid, reservationQuoteData, reservationGuid, category): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/create/product/category/${category}`;
		return this.http.post(url, { reservationGuid, ...reservationQuoteData }, { headers: { 'x-facility-guid': facilityGuid } });
	}

	/*
	 **-------------------------------------------------------------------------------------
	 ** METHOD NAME - saveProductInsuranceQuotes
	 **-------------------------------------------------------------------------------------
	 */
	public saveProductInsuranceQuotes(
		facilityGuid,
		quoteGuid,
		reservationGuid,
		rules,
		company,
		manifestData,
		category
	): Observable<any> {
		const quotesServiceUrl = this.env.get<string>('insuranceQuotesEndPoint');
		const url = `${quotesServiceUrl}/${quoteGuid}/update/category/${category}`;
		return this.http.put(
			url,
			{ selection: true, reservationGuid, rules, company, payload: manifestData },
			{ headers: { 'x-facility-guid': facilityGuid } }
		);
	}
}
